import { z } from 'zod';
import { DEFAULT_PLATFORM_PERCENT } from './billing.js';
export const LocationTypeSchema = z.enum([
    'residential', 'multifamily', 'commercial', 'public', 'fleet',
]);
export const CoordinatesSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
export const AddressSchema = z.object({
    street: z.string().min(1),
    city: z.string().min(1),
    state: z.string().min(2).max(3),
    zip: z.string().min(1),
    country: z.string().length(2).default('US'),
    coordinates: CoordinatesSchema.optional(),
});
export const ElectricityBillingTypeSchema = z.enum(['per_kwh', 'flat_monthly']);
/**
 * Inline TOU windows for Location electricity reimbursement —
 * Tariff-shaped but with `rate_cents` instead of the `energy_rate`
 * Tariff uses, so a casual reader of the schema doesn't confuse
 * a building-side reimbursement rate with the customer-facing
 * tariff rate. Same `day_of_week` / `start_hour` / `end_hour`
 * matching semantics as TouScheduleEntry — see
 * `effectiveTariffRate` for the resolution rules.
 *
 * Distinct from `electricity_tariff_id`: an inline window list
 * lets a building admin define a TOU schedule WITHOUT having to
 * create a Tariff doc just to be referenced once. The three-mode
 * priority on read is:
 *   1. electricity_tariff_id set (TOU resolved on the Tariff)
 *   2. electricity_tou_windows non-empty (inline TOU)
 *   3. flat electricity_rate_cents
 */
/**
 * Per-window unit selector for Location TOU. `per_kwh` (default,
 * historical behavior) charges energy_in_window × rate_cents.
 * `per_hour` charges duration_in_window_hours × rate_cents — used
 * for idle-fee windows or time-billed pricing models where the
 * building reimburses the operator on a per-hour basis instead of
 * per-kWh. Defaults to `per_kwh` so legacy docs without the field
 * round-trip unchanged.
 */
export const ElectricityRateTypeSchema = z.enum(['per_kwh', 'per_hour']);
export const ElectricityTouWindowSchema = z.object({
    day_of_week: z.array(z.number().int().min(0).max(6)),
    start_hour: z.number().int().min(0).max(23),
    end_hour: z.number().int().min(1).max(24),
    rate_cents: z.number().nonnegative(),
    rate_type: ElectricityRateTypeSchema.default('per_kwh'),
});
export const LocationSchema = z.object({
    _id: z.string(),
    tenant_id: z.string(),
    name: z.string().min(1),
    address: AddressSchema,
    type: LocationTypeSchema,
    charger_count: z.number().int().nonnegative(),
    is_active: z.boolean(),
    /** Location's electricity cost in cents/kWh (for per_kwh) or cents/month (flat) */
    electricity_rate_cents: z.number().nonnegative().default(0),
    /**
     * Unit selector for the FLAT branch of `electricity_rate_cents`.
     * 'per_kwh' (default, historical): cost = energy_kwh × rate.
     * 'per_hour': cost = duration_hours × rate. Used for buildings
     * that bill operators by occupancy time rather than energy.
     *
     * Distinct from `electricity_billing_type` below — that's the
     * legacy 'per_kwh' vs 'flat_monthly' classifier on the cost
     * structure side. `electricity_rate_type` controls how the
     * billing CALCULATION dispatches when the resolver lands on
     * the flat branch (no TOU, no Tariff ref).
     *
     * Inline TOU windows carry their own per-window rate_type
     * (Commit B); the Tariff entity carries a tariff-level
     * rate_type (Commit D). This is the third leg for the flat
     * fallback path.
     */
    electricity_rate_type: ElectricityRateTypeSchema.default('per_kwh'),
    electricity_billing_type: ElectricityBillingTypeSchema.default('per_kwh'),
    /** EVI's platform share, 0-100 (percent). Default DEFAULT_PLATFORM_PERCENT
     *  (15). A new session pins max(this, PLATFORM_FEE_FLOOR_BPS/100). */
    revenue_split: z.number().min(0).max(100).default(DEFAULT_PLATFORM_PERCENT),
    /** Default tariff for chargers at this location */
    default_tariff_id: z.string().optional(),
    /** Optional Tariff ref driving electricity reimbursement (TOU
     *  windows pinned to Session at start time per commits 1.2/1.3
     *  of resident-managed-chargers). Null falls back to the flat
     *  `electricity_rate_cents` above. */
    electricity_tariff_id: z.string().nullable().optional(),
    /** Inline TOU windows — second-priority reimbursement source,
     *  used when electricity_tariff_id is null and the windows
     *  array is non-empty. Three-mode resolution lives in
     *  @evi/billing's resolveLocationElectricityRate. */
    electricity_tou_windows: z.array(ElectricityTouWindowSchema).nullable().optional(),
    /**
     * Google Maps place_id for this Location (Phase 2 of the
     * gamification rules update — used by the post-session
     * review CTA in Phase 3). Trimmed, non-empty when present,
     * fully optional. No regex shape — Google's place_id
     * variants make strict validation brittle. Trimming on the
     * Zod side mirrors the Mongoose `trim: true` so client
     * payloads with stray whitespace round-trip cleanly without
     * a server-side cleanup pass.
     */
    google_place_id: z.string().trim().min(1).optional(),
    /**
     * Per-Location resident-managed-chargers gate (Phase 1 of the
     * tenant→location toggle migration). Defaults true (opt-OUT)
     * matching the legacy tenant-level field's semantics.
     * `.optional()` on the schema for backward compatibility with
     * old API responses that pre-date the field — the Mongoose
     * model's `default: true` ensures live reads from the DB
     * always include it post-migration. Phase 2 wires the API
     * gate + PWA reads to consume this field.
     */
    allow_resident_managed_chargers: z.boolean().default(true).optional(),
    /** Per-building fob self-registration switch. Opt-IN: default false. */
    allow_resident_fob_registration: z.boolean().default(false).optional(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
export const CreateLocationSchema = LocationSchema.omit({
    _id: true, tenant_id: true, charger_count: true, created_at: true, updated_at: true,
});
//# sourceMappingURL=location.js.map